// Service Worker Manager Service
// Manages interactions with the service worker for version checking and data reload

// Extend Window interface to include APP_ENV
declare global {
  interface Window {
    APP_ENV?: {
      VERSION: string;
      BUILD_DATE: string;
      BUILD_TIME: string;
      BUILD_TIMESTAMP: string;
      BUILD_UNIX: number;
      NODE_ENV: string;
    };
  }
}

export interface VersionInfo {
  oldVersion: string;
  newVersion: string;
  timestamp: string;
}

export interface CacheInfo {
  name: string;
  size: number;
}

export interface ServiceWorkerMessage {
  type: string;
  payload?: any;
}

export class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private messageHandlers: Map<string, Function> = new Map();
  private isInitialized = false;

  constructor() {
    this.setupMessageHandlers();
  }

  // Check if running in WebView
  private isWebView(): boolean {
    // Common WebView detection patterns
    const userAgent = navigator.userAgent.toLowerCase();
    const isWebView = 
      userAgent.includes('utilitytool') ||
      userAgent.includes('wv') || // Android WebView
      (userAgent.includes('mobile') && userAgent.includes('safari') && !userAgent.includes('chrome')) || // iOS WebView
      userAgent.includes('electron') || // Electron WebView
      userAgent.includes('cordova') || // Cordova/PhoneGap
      userAgent.includes('capacitor') || // Capacitor
      userAgent.includes('react-native') || // React Native WebView
      (window as any).ReactNativeWebView !== undefined; // React Native WebView global
    
    return isWebView;
  }

  // Initialize service worker
  async initialize(): Promise<boolean> {
    try {
      // Skip service worker in WebView environments
      if (this.isWebView()) {
        this.isInitialized = true; // Mark as initialized to prevent retries
        return true;
      }

      if ('serviceWorker' in navigator) {
        // Development mode handling
        if (process.env.NODE_ENV === 'development') {
          // In development, we're more lenient with service worker initialization
          try {
            // Try to get existing registration first
            const existingRegistration = await navigator.serviceWorker.getRegistration();
            if (existingRegistration) {
              this.registration = existingRegistration;
            } else {
              // Wait for service worker with shorter timeout in dev mode
              const devTimeoutPromise = new Promise<never>((_, reject) => {
                setTimeout(() => reject(new Error('Service Worker not ready in dev mode after 5 seconds')), 5000);
              });

              this.registration = await Promise.race([
                navigator.serviceWorker.ready,
                devTimeoutPromise
              ]);
              console.log('✅ Development: Service Worker registered successfully');
            }
          } catch (devError) {
            console.warn('⚠️ Development: Service Worker not ready, but continuing...', devError);
            
            // In development, don't fail completely - return true to allow app to continue
            this.isInitialized = true;
            return true;
          }
        } else {
          // Production mode - strict service worker requirements
          if (navigator.serviceWorker.controller) {
            // Service worker is already active
            this.registration = await navigator.serviceWorker.getRegistration();
          } else {
            // Wait for service worker to be ready with production timeout
            const timeoutPromise = new Promise<never>((_, reject) => {
              setTimeout(() => reject(new Error('Service Worker not ready after 10 seconds')), 10000);
            });

            try {
              this.registration = await Promise.race([
                navigator.serviceWorker.ready,
                timeoutPromise
              ]);
            } catch (timeoutError) {
              console.warn('⚠️ Production: Service Worker not ready yet, will retry later');
              return false;
            }
          }
        }
        
        // Listen for service worker messages
        navigator.serviceWorker.addEventListener('message', this.handleServiceWorkerMessage.bind(this));
        
        // Listen for service worker updates
        this.registration.addEventListener('updatefound', this.handleUpdateFound.bind(this));
        
        // Listen for service worker state changes
        this.registration.addEventListener('statechange', this.handleStateChange.bind(this));
        
        // Clear update flags if service worker is active
        if (this.registration.active && this.registration.active.state === 'activated') {
          sessionStorage.removeItem('sw-update-in-progress');
          sessionStorage.removeItem('sw-update-version');
        }
        
        this.isInitialized = true;
        
        // Check version on initialization (non-blocking)
        this.checkVersion().catch(error => {
          console.warn('Version check failed during initialization:', error);
        });
        
        return true;
      } else {
        console.warn('Service Worker not supported');
        return false;
      }
    } catch (error) {
      console.error('Failed to initialize Service Worker manager:', error);
      // Don't fail completely - return false but don't block the app
      return false;
    }
  }

  // Retry initialization with exponential backoff
  async retryInitialization(maxRetries: number = 3): Promise<boolean> {
    // Skip retries if running in WebView
    if (this.isWebView()) {
      return true;
    }

    // In development mode, be more lenient with retries
    if (process.env.NODE_ENV === 'development') {
      maxRetries = Math.min(maxRetries, 2); // Fewer retries in dev mode
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 Retrying Service Worker initialization (attempt ${attempt}/${maxRetries})`);
        
        // Wait before retry with exponential backoff
        if (attempt > 1) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000); // Max 10 seconds
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        const success = await this.initialize();
        if (success) {
          console.log('✅ Service Worker initialization succeeded on retry');
          return true;
        }
      } catch (error) {
        console.warn(`⚠️ Retry attempt ${attempt} failed:`, error);
      }
    }
    
    // In development mode, don't fail completely
    if (process.env.NODE_ENV === 'development') {
      return true; // Allow app to continue in dev mode
    }
    
    console.error('❌ Service Worker initialization failed after all retries');
    return false;
  }

  // Setup message handlers
  private setupMessageHandlers() {
    this.messageHandlers.set('VERSION_CHANGED', this.onVersionChanged.bind(this));
    this.messageHandlers.set('BACKGROUND_SYNC', this.onBackgroundSync.bind(this));
    this.messageHandlers.set('FORCE_UPDATE', this.onForceUpdate.bind(this));
    this.messageHandlers.set('FOCUS_CLIENT', this.onFocusClient.bind(this));
  }

  // Handle messages from service worker
  private handleServiceWorkerMessage(event: MessageEvent) {
    const { type, payload } = event.data;
    
    const handler = this.messageHandlers.get(type);
    if (handler) {
      handler(payload);
    } else {
      console.log('Unhandled service worker message:', type, payload);
    }
  }

  // Handle service worker update found
  private handleUpdateFound() {
    console.log('Service Worker update found');
    
    if (this.registration?.waiting) {
      // Notify user about available update
      this.notifyUpdateAvailable();
    }
  }

  // Handle service worker state change
  private handleStateChange() {
    if (this.registration?.installing) {
      console.log('Service Worker installing...');
    } else if (this.registration?.waiting) {
      console.log('Service Worker waiting...');
    } else if (this.registration?.active) {
      console.log('Service Worker active');
      
      // Clear update flags when service worker becomes active
      sessionStorage.removeItem('sw-update-in-progress');
      sessionStorage.removeItem('sw-update-version');
    }
  }

  // Check current version
  async checkVersion(): Promise<{ versionChanged: boolean; currentVersion: string }> {
    // In WebView, always return DOM version (no service worker version checking)
    if (this.isWebView()) {
      const domVersion = this.getVersionFromDOM();
      return {
        versionChanged: false,
        currentVersion: domVersion
      };
    }

    // Development mode handling - be more lenient
    if (process.env.NODE_ENV === 'development') {
      
      if (!this.registration?.active) {
        const domVersion = this.getVersionFromDOM();
        return {
          versionChanged: false,
          currentVersion: domVersion
        };
      }
    }

    // Don't check version if we're in the middle of updating
    if (sessionStorage.getItem('sw-update-in-progress') === 'true') {
      const fallbackVersion = this.getVersionFromDOM();
      return {
        versionChanged: false,
        currentVersion: fallbackVersion
      };
    }
    
    if (!this.registration?.active) {
      // Fallback to DOM version if service worker is not active
      const fallbackVersion = this.getVersionFromDOM();
      return {
        versionChanged: false,
        currentVersion: fallbackVersion
      };
    }

    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();
      
      // Set a timeout in case the service worker doesn't respond
      const timeout = setTimeout(() => {
        const fallbackVersion = this.getVersionFromDOM();
        resolve({
          versionChanged: false,
          currentVersion: fallbackVersion
        });
      }, 10000); // Increased to 10 seconds

      // Add connection timeout check
      const connectionTimeout = setTimeout(() => {
        if (this.registration?.active?.state !== 'activated') {
          clearTimeout(timeout);
          const fallbackVersion = this.getVersionFromDOM();
          resolve({
            versionChanged: false,
            currentVersion: fallbackVersion
          });
        } else {
          console.log('Service Worker is active, waiting for response...');
        }
      }, 3000); // Check connection after 3 seconds

      messageChannel.port1.onmessage = (event) => {
        clearTimeout(timeout);
        clearTimeout(connectionTimeout);
        const { type, payload } = event.data;
        
        if (type === 'VERSION_INFO') {
          resolve(payload);
        } else if (type === 'VERSION_CHECK_ERROR' || type === 'CACHE_CLEAR_ERROR' || type === 'FORCE_UPDATE_ERROR') {
          console.error('❌ Service Worker error:', type, payload);
          const fallbackVersion = this.getVersionFromDOM();
          resolve({
            versionChanged: false,
            currentVersion: fallbackVersion
          });
        } else {
          const fallbackVersion = this.getVersionFromDOM();
          resolve({
            versionChanged: false,
            currentVersion: fallbackVersion
          });
        }
      };


      this.registration!.active!.postMessage(
        { type: 'GET_VERSION' },
        [messageChannel.port2]
      );
    });
  }

  // Clear data cache
  async clearDataCache(): Promise<void> {
    // In WebView, no service worker cache to clear
    if (this.isWebView()) {
      return;
    }

    if (!this.registration?.active) {
      throw new Error('Service Worker not active');
    }

    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();
      const timeout = setTimeout(() => {
        reject(new Error('Cache clear operation timeout'));
      }, 10000); // 10 second timeout
      
      messageChannel.port1.onmessage = (event) => {
        clearTimeout(timeout);
        const { type, payload } = event.data;
        
        if (type === 'DATA_CACHE_CLEARED') {
          console.log('✅ Data cache cleared successfully');
          resolve();
        } else if (type === 'CACHE_CLEAR_ERROR') {
          reject(new Error(payload?.error || 'Cache clear failed'));
        } else {
          reject(new Error(`Unexpected response type: ${type}`));
        }
      };

      console.log('🧹 Sending CLEAR_DATA_CACHE message to service worker...');
      this.registration!.active!.postMessage(
        { type: 'CLEAR_DATA_CACHE' },
        [messageChannel.port2]
      );
    });
  }

  // Get version from DOM as fallback
  getVersionFromDOM(): string {
    try {
      // Try to get version from root element data attribute
      const rootElement = document.getElementById('root');
      if (rootElement && rootElement.getAttribute('data-version')) {
        return rootElement.getAttribute('data-version') || 'unknown';
      }
      
      // Try to get version from env.js
      if (window.APP_ENV && window.APP_ENV.VERSION) {
        return window.APP_ENV.VERSION;
      }
      
      // Try to get version from meta tag
      const generatorMeta = document.querySelector('meta[name="generator"]');
      if (generatorMeta) {
        const content = generatorMeta.getAttribute('content');
        if (content && content.includes(' ')) {
          return content.split(' ').pop() || 'unknown';
        }
      }
      
      return 'unknown';
    } catch (error) {
      console.warn('Failed to get version from DOM:', error);
      return 'unknown';
    }
  }

  // Get cache information
  async getCacheInfo(): Promise<CacheInfo[]> {
    // In WebView, no service worker cache info available
    if (this.isWebView()) {
      return [];
    }

    if (!this.registration?.active) {
      throw new Error('Service Worker not active');
    }

    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();
      const timeout = setTimeout(() => {
        reject(new Error('Cache info operation timeout'));
      }, 10000); // 10 second timeout
      
      messageChannel.port1.onmessage = (event) => {
        clearTimeout(timeout);
        if (event.data && event.data.type) {
          const { type, payload } = event.data;
          
          if (type === 'CACHE_INFO') {
            resolve(payload.caches || []);
          } else {
            reject(new Error(`Unexpected response type: ${type}`));
          }
        } else {
          reject(new Error('Invalid message format from service worker'));
        }
      };

      // Check if postMessage is available
      if (this.registration.active.postMessage) {
        this.registration.active.postMessage(
          { type: 'GET_CACHE_INFO' },
          [messageChannel.port2]
        );
      } else {
        clearTimeout(timeout);
        reject(new Error('Service worker postMessage not available'));
      }
    });
  }

  // Skip waiting and activate new service worker
  async skipWaiting(): Promise<void> {
    // In WebView, no service worker to skip waiting for
    if (this.isWebView()) {
      return;
    }

    if (this.registration?.waiting) {
      try {
        // Store the current version before updating
        const currentVersion = this.getVersionFromDOM();
        
        // Mark that we're updating to prevent showing the popup again
        sessionStorage.setItem('sw-update-in-progress', 'true');
        sessionStorage.setItem('sw-update-version', currentVersion);
        
        this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        
        // Wait for the new service worker to activate with timeout
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Service Worker activation timeout'));
          }, 30000); // 30 second timeout for activation
          
          const checkState = () => {
            if (this.registration?.active?.state === 'activated') {
              clearTimeout(timeout);
              console.log('✅ Service Worker activated successfully');
              resolve();
            } else {
              setTimeout(checkState, 100);
            }
          };
          checkState();
        });
      } catch (error) {
        console.error('❌ Skip waiting failed:', error);
        throw error;
      }
    } else {
      console.log('ℹ️ No waiting service worker to skip');
    }
  }

  // Force reload the page
  reloadPage(): void {
    // Use the safer reload method
    this.safeReloadPage();
  }

  // Force update by clearing all caches and reloading
  async forceUpdate(): Promise<void> {
    try {
      
      // In WebView, just reload the page (no service worker cache clearing)
      if (this.isWebView()) {
        this.reloadPage();
        return;
      }
      
      if (this.registration?.active) {
        // Send force update message to service worker
        const messageChannel = new MessageChannel();
        
        const updatePromise = new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            resolve();
          }, 15000); // 15 second timeout for force update
          
          messageChannel.port1.onmessage = (event) => {
            clearTimeout(timeout);
            if (event.data && event.data.type) {
              const { type, payload } = event.data;
              
              if (type === 'FORCE_UPDATE_COMPLETED') {
                resolve();
              } else if (type === 'FORCE_UPDATE_ERROR') {
                reject(new Error(payload?.error || 'Force update failed'));
              } else {
                console.warn('⚠️ Unexpected response during force update:', type);
                resolve(); // Continue anyway
              }
            } else {
              console.warn('⚠️ Invalid message format during force update');
              resolve(); // Continue anyway
            }
          };
        });

        // Check if postMessage is available
        if (this.registration.active.postMessage) {
          this.registration.active.postMessage(
            { type: 'FORCE_UPDATE' },
            [messageChannel.port2]
          );
        } else {
          console.warn('⚠️ Service worker postMessage not available');
        }

        await updatePromise;
      } else {
        // If no service worker, just reload
        console.log('⚠️ No service worker active, reloading directly...');
      }
      
      // Always reload after force update
      this.reloadPage();
    } catch (error) {
      console.error('❌ Force update failed:', error);
      // Fallback to reload
      this.reloadPage();
    }
  }

  // Clear update state (for debugging/testing)
  clearUpdateState(): void {
    try {
      sessionStorage.removeItem('sw-update-in-progress');
      sessionStorage.removeItem('sw-update-version');
      console.log('✅ Update state cleared');
    } catch (error) {
      console.error('❌ Failed to clear update state:', error);
    }
  }

  // Check if service worker is responsive
  async isServiceWorkerResponsive(): Promise<boolean> {
    // In WebView, service worker is not available
    if (this.isWebView()) {
      return false;
    }

    if (!this.registration?.active) {
      console.log('ℹ️ No active service worker to check');
      return false;
    }

    try {
      const messageChannel = new MessageChannel();
      const responsePromise = new Promise<boolean>((resolve) => {
        const timeout = setTimeout(() => {
          resolve(false);
        }, 3000);
        
        messageChannel.port1.onmessage = (event) => {
          clearTimeout(timeout);
          const { type } = event.data;
          if (type === 'VERSION_INFO') {
            resolve(true);
          } else {
            resolve(false);
          }
        };
      });

      this.registration.active.postMessage(
        { type: 'GET_VERSION' },
        [messageChannel.port2]
      );

      return await responsePromise;
    } catch (error) {
      return false;
    }
  }

  // Force reload service worker (for debugging)
  async forceReloadServiceWorker(): Promise<void> {
    try {
      // In WebView, no service worker to reload
      if (this.isWebView()) {
        window.location.reload();
        return;
      }
      
      if (this.registration) {
        // Unregister current service worker
        await this.registration.unregister();
        // Clear all caches
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(name => caches.delete(name))
        );
        console.log('✅ All caches cleared');
        
        // Reload page to trigger new service worker registration
        window.location.reload();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('❌ Failed to force reload service worker:', error);
      // Fallback to page reload
      window.location.reload();
    }
  }

  // Notify user about available update
  private notifyUpdateAvailable(): void {
    // You can customize this notification
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('App Update Available', {
        body: 'A new version is available. Click to update.',
        icon: '/icon-192x192.png',
        requireInteraction: true
      });
    }
    
    // Or show a custom UI notification
    this.showUpdateNotification();
  }

  // Show custom update notification
  private showUpdateNotification(): void {
    const notification = document.createElement('div');
    notification.className = 'update-notification';
    notification.innerHTML = `
      <div class="update-notification-content">
        <h4>🔄 App Update Available</h4>
        <p>A new version is available. Update now to get the latest features.</p>
        <div class="update-notification-actions">
          <button class="btn btn-primary" id="update-now">Update Now</button>
          <button class="btn btn-secondary" id="update-later">Later</button>
        </div>
      </div>
    `;

    // Event: Update Now
    notification.querySelector('#update-now')?.addEventListener('click', async () => {
      const updateButton = notification.querySelector('#update-now') as HTMLButtonElement;
      if (updateButton) {
        updateButton.textContent = 'Updating...';
        updateButton.disabled = true;
      }

      try {
        await this.skipWaiting();

        // Reload when new SW takes control
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          this.showUpdateSuccess(notification);
          setTimeout(() => this.safeReloadPage(), 2000);
        });

      } catch (error) {
        console.error('Update failed:', error);
        if (updateButton) {
          updateButton.textContent = 'Update Now';
          updateButton.disabled = false;
        }
        this.showUpdateError(notification);
      }
    });

    // Event: Later
    notification.querySelector('#update-later')?.addEventListener('click', () => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    });

    document.body.appendChild(notification);

    // Auto-remove sau 30s
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 30000);
  }

  // Show update success message
  private showUpdateSuccess(notification: HTMLElement): void {
    const content = notification.querySelector('.update-notification-content');
    if (content) {
      content.innerHTML = `
        <h4>✅ Update Successful</h4>
        <p>Application updated successfully! The page will reload in a few seconds...</p>
        <div class="update-notification-actions">
          <button class="btn btn-primary" disabled>Reloading...</button>
        </div>
      `;

      // Progress bar
      const progressBar = document.createElement('div');
      progressBar.className = 'update-progress-bar';

      const progressFill = document.createElement('div');
      progressFill.className = 'update-progress-fill';

      progressBar.appendChild(progressFill);
      content.appendChild(progressBar);

      // Animate
      requestAnimationFrame(() => {
        progressFill.style.width = '100%';
      });
    }
  }

  // Show update error message
  private showUpdateError(notification: HTMLElement): void {
    const content = notification.querySelector('.update-notification-content');
    if (content) {
      content.innerHTML = `
        <h4>❌ Update Failed</h4>
        <p>Failed to update the application. Please refresh the page manually.</p>
        <div class="update-notification-actions">
          <button class="btn btn-primary" id="refresh-page">Refresh Page</button>
          <button class="btn btn-secondary" id="close-notification">Close</button>
        </div>
      `;

      content.querySelector('#refresh-page')?.addEventListener('click', () => {
        window.location.reload();
      });

      content.querySelector('#close-notification')?.addEventListener('click', () => {
        const parent = notification.parentElement;
        if (parent) parent.removeChild(notification);
      });
    }
  }

  // Safe reload method that handles service worker updates
  private safeReloadPage(): void {
    const currentUrl = window.location.href;
    try {
      // Check if we're in a browser environment
      if (typeof window !== 'undefined' && window.location) {
        // Clear any potential corrupted chunks from memory
        if (window.location.reload) {  
          // Clear webpack chunks and caches that might be corrupted
          this.clearWebpackCache();
          
          // Clear update progress from session storage
          this.clearUpdateProgress();
          
          // Force a hard reload to bypass cache
          window.location.reload();
        } else {
          // Fallback for environments where reload might not be available
          console.warn('⚠️ window.location.reload not available, using fallback');
          window.location.href = currentUrl;
        }
      } else {
        console.warn('⚠️ Not in browser environment, cannot reload');
      }
    } catch (error) {
      console.error('❌ Error during safe page reload:', error);
      // Last resort fallback - try to navigate to the same page
      try {
        if (typeof window !== 'undefined') {
          window.location.href = currentUrl;
        }
      } catch (fallbackError) {
        console.error('❌ Fallback reload also failed:', fallbackError);
      }
    }
  }

  // Clear update progress from session storage
  private clearUpdateProgress(): void {
    try {
      sessionStorage.removeItem('sw-update-in-progress');
      sessionStorage.removeItem('sw-update-version');
      console.log('✅ Cleared update progress from session storage');
    } catch (error) {
      console.warn('⚠️ Could not clear update progress:', error);
    }
  }

  // Clear webpack cache and chunks to prevent corruption
  private clearWebpackCache(): void {
    try {
      // Clear webpack chunk cache
      if ((window as any).webpackChunkload) {
        try {
          (window as any).webpackChunkload = undefined;
          console.log('✅ Cleared webpack chunk cache');
        } catch (e) {
          console.warn('⚠️ Could not clear webpack chunk cache:', e);
        }
      }

      // Clear any other webpack-related globals that might cause issues
      const webpackGlobals = [
        'webpackChunkload',
        'webpackJsonp',
        'webpackHotUpdate',
        '__webpack_require__',
        '__webpack_public_path__'
      ];

      webpackGlobals.forEach(globalName => {
        if ((window as any)[globalName]) {
          try {
            (window as any)[globalName] = undefined;
          } catch (e) {
            console.warn(`⚠️ Could not clear ${globalName}:`, e);
          }
        }
      });

      // Clear any cached chunks in memory
      if ((window as any).__webpack_require__ && (window as any).__webpack_require__.c) {
        try {
          (window as any).__webpack_require__.c = {};
          console.log('✅ Cleared webpack require cache');
        } catch (e) {
          console.warn('⚠️ Could not clear webpack require cache:', e);
        }
      }

      // Clear any service worker caches that might be corrupted
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            if (cacheName.includes('webpack') || cacheName.includes('chunk')) {
              caches.delete(cacheName).then(() => {
                console.log(`✅ Cleared cache: ${cacheName}`);
              }).catch(e => {
                console.warn(`⚠️ Could not clear cache ${cacheName}:`, e);
              });
            }
          });
        }).catch(e => {
          console.warn('⚠️ Could not access caches:', e);
        });
      }

      console.log('✅ Webpack cache clearing completed');
    } catch (error) {
      console.warn('⚠️ Error during webpack cache clearing:', error);
    }
  }

  // Message handlers
  private onVersionChanged(payload: VersionInfo): void {
    console.log('Version changed:', payload);
    
    // Clear local storage if needed
    this.clearLocalData();
    
    // Reload page to apply new version
    this.reloadPage();
  }

  private onBackgroundSync(payload: any): void {
    console.log('Background sync triggered:', payload);
    
    // Perform any necessary data synchronization
    this.performDataSync();
  }

  private onForceUpdate(payload: any): void {
    console.log('Force update triggered:', payload);
    
    // Force reload the page
    this.reloadPage();
  }

  private onFocusClient(payload: any): void {
    console.log('Focus client requested:', payload);
    
    // Focus the current window/tab
    window.focus();
  }

  // Clear local data when version changes
  private clearLocalData(): void {
    try {
      // Only clear app-specific data, not all caches
      // This prevents interference with the main service worker
      
      // Clear app-specific localStorage keys
      const appKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('app-') || 
        key.startsWith('pos-') || 
        key.startsWith('user-')
      );
      appKeys.forEach(key => localStorage.removeItem(key));
      
      // Clear app-specific sessionStorage keys
      const appSessionKeys = Object.keys(sessionStorage).filter(key => 
        key.startsWith('app-') || 
        key.startsWith('pos-') || 
        key.startsWith('user-')
      );
      appSessionKeys.forEach(key => sessionStorage.removeItem(key));
      
      // Only clear app-specific caches, not all caches
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          const appCaches = cacheNames.filter(cacheName => 
            cacheName.startsWith('app-') || 
            cacheName.startsWith('pos-') || 
            cacheName.startsWith('data-')
          );
          appCaches.forEach(cacheName => {
            caches.delete(cacheName);
          });
        });
      }
      
      console.log('App-specific data cleared successfully');
    } catch (error) {
      console.error('Error clearing app data:', error);
    }
  }

  // Perform data synchronization
  private async performDataSync(): Promise<void> {
    try {
      // Sync any pending data with server
      // This is where you would implement your sync logic
      console.log('Performing data synchronization...');
      
      // Example: sync offline data
      // await this.syncOfflineData();
      
    } catch (error) {
      console.error('Data synchronization failed:', error);
    }
  }

  // Get service worker status
  getStatus(): { isInitialized: boolean; state: string | null } {
    return {
      isInitialized: this.isInitialized,
      state: this.registration?.active?.state || null
    };
  }

  // Check if update is available
  isUpdateAvailable(): boolean {
    // Don't show update if we're in the middle of updating
    if (sessionStorage.getItem('sw-update-in-progress') === 'true') {
      return false;
    }
    
    // Check if there's a waiting service worker
    return this.registration?.waiting !== undefined;
  }

  // Cleanup
  destroy(): void {
    if (this.registration) {
      this.registration.removeEventListener('updatefound', this.handleUpdateFound.bind(this));
      this.registration.removeEventListener('statechange', this.handleStateChange.bind(this));
    }
    
    if (navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', this.handleServiceWorkerMessage.bind(this));
    }
    
    this.isInitialized = false;
    this.registration = null;
  }
}

// Export singleton instance
export const serviceWorkerManager = new ServiceWorkerManager();

// Global force update function for easy access
declare global {
  interface Window {
    serviceWorkerManager: ServiceWorkerManager;
    forceAppUpdate: () => Promise<void>;
  }
}

// Make service worker manager globally accessible
if (typeof window !== 'undefined') {
  window.serviceWorkerManager = serviceWorkerManager;
  
  // Global force update function
  window.forceAppUpdate = async () => {
    try {
      console.log('🚀 Global force update called...');
      await serviceWorkerManager.forceUpdate();
    } catch (error) {
      console.error('❌ Global force update failed:', error);
      // Fallback to page reload
      window.location.reload();
    }
  };
}
